/**
 * Shows a fixed bottom bar when the main PDP add-to-cart leaves the viewport (mobile).
 * Syncs label, disabled state, and price from the primary controls.
 */
(function () {
  function syncText(from, to) {
    var a = from && from.querySelector('span');
    var b = to && to.querySelector('.dlux-sticky-atc__btn-text');
    if (!a || !b) return;
    var t = a.textContent.trim();
    if (t) b.textContent = t;
  }

  function init(root) {
    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return;

    var mainBtn = document.getElementById('ProductSubmitButton-' + sectionId);
    var priceEl = document.getElementById('price-' + sectionId);
    var stickyBtn = document.getElementById('DluxStickySubmit-' + sectionId);
    var stickyPrice = document.getElementById('DluxStickyPrice-' + sectionId);

    if (!mainBtn || !stickyBtn) return;

    var form = document.getElementById(stickyBtn.getAttribute('form'));
    if (form) {
      stickyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (stickyBtn.disabled) return;
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          mainBtn.click();
        }
      });
    }

    var mq = window.matchMedia('(min-width: 0px)');

    // Mobile gets the bar from page load. Desktop keeps the original behaviour.
    var mqMobile = window.matchMedia('(max-width: 749px)');

    function setVisible(visible) {
      if (!mq.matches) {
        root.hidden = true;
        root.setAttribute('aria-hidden', 'true');
        root.classList.remove('is-visible');
        document.documentElement.style.removeProperty('--dlux-sticky-atc-height');
        return;
      }
      root.hidden = !visible;
      root.setAttribute('aria-hidden', visible ? 'false' : 'true');
      root.classList.toggle('is-visible', visible);
      if (visible) {
        document.documentElement.style.setProperty('--dlux-sticky-atc-height', root.offsetHeight + 'px');
      } else {
        document.documentElement.style.removeProperty('--dlux-sticky-atc-height');
      }
    }

    // Show the bar whenever the main add-to-cart is NOT on screen.
    //
    // The original rule was `bottom <= 0`, i.e. show only once the main button had
    // scrolled off the TOP. The reasoning was that while the button is still below the
    // fold the shopper has not reached it yet, so a pinned bar would duplicate a control
    // they are about to see. On this theme that assumption does not hold. On a 390px
    // viewport the gallery alone runs ~495-565px before the h1, and the buy row lands
    // roughly 1150-1250px down the document. Clarity puts average mobile PDP scroll depth
    // at 23.4% with 20.3s active time, so the median mobile shopper never reaches the
    // button at all and, under the old rule, never saw a buy control of any kind.
    //
    // So on mobile: visible from page load, hidden only while the real button is actually
    // in view, visible again once it passes. Desktop keeps `bottom <= 0`, where the buy
    // row sits beside the gallery and is above the fold anyway.
    //
    // Measured live rather than from `entry.boundingClientRect`: that rect is captured
    // at the threshold crossing, where bottom is ~0 and can round positive, and once the
    // button is fully outside the observer never fires again - so the bar never appeared.
    function update() {
      var rect = mainBtn.getBoundingClientRect();

      if (!mqMobile.matches) {
        setVisible(rect.bottom <= 0);
        return;
      }

      var vh = window.innerHeight || document.documentElement.clientHeight;
      var mainBtnOnScreen = rect.bottom > 0 && rect.top < vh;
      setVisible(!mainBtnOnScreen);

      // Do not repeat a price the shopper can already see. At the top of a PDP the
      // main price block is in view while the buy row is still ~500px below, so the
      // bar would show a second copy of the same figure a few pixels under the first.
      // In that state the bar drops the price and runs the button full width.
      if (priceEl) {
        var pr = priceEl.getBoundingClientRect();
        var priceOnScreen = pr.bottom > 0 && pr.top < vh;
        root.classList.toggle('is-price-hidden', priceOnScreen);
      }
    }

    // Scroll is the source of truth. The observer stays as a cheap extra trigger for
    // moves that happen without a scroll event (variant swaps resizing the buy row,
    // accordions opening above it).
    var ticking = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        update();
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    var io = new IntersectionObserver(update, { root: null, rootMargin: '0px', threshold: 0 });
    io.observe(mainBtn);

    // Mirror the MAIN button's busy state onto the sticky one.
    //
    // product-form.js owns the add. On submit it sets `aria-disabled` and adds
    // `loading` to the button INSIDE <product-form>, which is the main buy box.
    // This bar sits outside that element, so before this fix the sticky button
    // showed no state change at all while a request was in flight. A shopper who
    // tapped again during a slow add hit the `aria-disabled` guard at the top of
    // onSubmitHandler and got nothing: no spinner, no error, no add. Clarity
    // records that as a dead click, and it needs no prior failure to happen, just
    // latency.
    //
    // The old version only copied `disabled`, which product-form.js never sets
    // during an add (it uses `aria-disabled`), and the observer was not even
    // watching that attribute. So the mirror could not have worked.
    // The busy state is carried by a spinner that REPLACES the label.
    //
    // History: a first attempt used Dawn's `.button.loading`, which sets
    // color:transparent and strokes the spinner with --color-button-text. This
    // button forces its own burgundy background with !important and never goes
    // through Dawn's button colour scheme, so that token resolved close to the
    // background and the spinner was invisible. The label was then swapped to
    // "Adding..." as a stopgap. The spinner stroke is now pinned to
    // --dlux-primary-foreground in dlux-sticky-atc.css, so it reads clearly, and
    // 2026-09-27 the label swap was dropped in favour of the spinner alone.
    //
    // Still never add `.loading` to this button. `.is-adding` hides the label with
    // visibility (so the button keeps its width) and centres the spinner. The
    // accessible name switches to BUSY_LABEL and aria-busy is set, so screen
    // readers still hear that the tap registered.
    var IDLE_LABEL = stickyBtn.getAttribute('aria-label') || '';
    var BUSY_LABEL = 'Adding to bag';

    function syncButton() {
      var busy = mainBtn.getAttribute('aria-disabled') === 'true';
      var spinning = mainBtn.classList.contains('loading');

      stickyBtn.disabled = mainBtn.disabled || busy;
      stickyBtn.setAttribute('aria-disabled', busy ? 'true' : 'false');

      // product-form.js unhides only the spinner inside <product-form>, so drive ours.
      var sp = stickyBtn.querySelector('.loading__spinner');
      if (sp) sp.classList.toggle('hidden', !spinning);

      stickyBtn.classList.toggle('is-adding', spinning);
      stickyBtn.setAttribute('aria-busy', spinning ? 'true' : 'false');
      if (IDLE_LABEL) stickyBtn.setAttribute('aria-label', spinning ? BUSY_LABEL : IDLE_LABEL);

      if (!spinning) syncText(mainBtn, stickyBtn);
    }

    var moBtn = new MutationObserver(syncButton);
    moBtn.observe(mainBtn, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
      subtree: true,
      childList: true,
      characterData: true
    });

    function syncPrice() {
      if (priceEl && stickyPrice) stickyPrice.innerHTML = priceEl.innerHTML;
    }

    if (priceEl && stickyPrice) {
      var moPrice = new MutationObserver(syncPrice);
      moPrice.observe(priceEl, { childList: true, subtree: true, characterData: true });
    }

    // Surface add errors ON the bar.
    //
    // product-form.js renders failures into `.product-form__error-message-wrapper`,
    // found with `this.querySelector`, which is scoped to <product-form>. Whenever
    // this bar is showing, the main buy box has scrolled off the top of the
    // viewport by definition, so the message painted somewhere the shopper could
    // not see and the tap looked dead. It already publishes cartError, so listen
    // for that rather than coupling product-form.js to this bar.
    var errEl = root.querySelector('[data-dlux-sticky-atc-error]');
    var errTimer = null;

    function clearStickyError() {
      if (!errEl) return;
      errEl.hidden = true;
      errEl.textContent = '';
    }

    function showStickyError(message) {
      // Only when the bar is actually on screen. If it is hidden the main buy box
      // is in view and already showing the same message, so this would duplicate it.
      if (!errEl || root.hidden) return;
      errEl.textContent = message;
      errEl.hidden = false;
      clearTimeout(errTimer);
      errTimer = setTimeout(clearStickyError, 8000);
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartError, function (event) {
        if (!event || event.source !== 'product-form') return;
        var msg = typeof event.errors === 'string' ? event.errors : event.message;
        showStickyError(msg || 'Could not add to bag. Please try again.');
      });
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'product-form') clearStickyError();
      });
    }

    stickyBtn.addEventListener('click', clearStickyError);

    syncButton();
    syncPrice();

    mq.addEventListener('change', function () {
      syncButton();
      syncPrice();
      if (!mq.matches) {
        setVisible(false);
        return;
      }
      requestAnimationFrame(update);
    });

    // Crossing the mobile breakpoint swaps which rule update() applies, so re-run it.
    mqMobile.addEventListener('change', function () {
      requestAnimationFrame(update);
    });

    // Paint the initial state. Without this the bar stays hidden until the first
    // scroll or resize, which is the whole point of showing it from page load.
    requestAnimationFrame(update);
  }

  document.querySelectorAll('[data-dlux-sticky-atc]').forEach(init);
})();
